<script setup lang="ts">
import { ref, watch } from 'vue'
import type { GameOptions } from '@/types'

const props = defineProps<{
  show: boolean
  options: GameOptions
  categories: any
}>()

const emit = defineEmits<{
  'update:show': [value: boolean]
  'update:options': [value: GameOptions]
}>()

const selectedCategories = ref<string[]>([])
const isRouletteMode = ref(false)

watch(() => props.options.categoryMode, (newMode) => {
  isRouletteMode.value = newMode === 'roulette'
  if (newMode !== 'roulette') {
    selectedCategories.value = []
  }
}, { immediate: true })

watch(() => props.options.selectedCategories, (newSelected) => {
  selectedCategories.value = [...newSelected]
}, { immediate: true })

const close = () => {
  emit('update:show', false)
}

const toggleCategory = (categoryKey: string) => {
  const index = selectedCategories.value.indexOf(categoryKey)
  if (index > -1) {
    selectedCategories.value.splice(index, 1)
  } else {
    selectedCategories.value.push(categoryKey)
  }
  emit('update:options', { 
    ...props.options, 
    selectedCategories: [...selectedCategories.value] 
  })
}

const isCategorySelected = (categoryKey: string) => {
  return selectedCategories.value.length === 0 || 
         selectedCategories.value.includes(categoryKey)
}

const setCategoryMode = (mode: 'roulette' | 'select') => {
  emit('update:options', {
    ...props.options,
    categoryMode: mode,
    selectedCategories: mode === 'roulette' ? [] : props.options.selectedCategories
  })
}

const selectSingleCategory = (categoryKey: string) => {
  emit('update:options', {
    ...props.options,
    selectedCategories: [categoryKey]
  })
}

const setDifficulty = (difficulty: 'fácil' | 'medio' | 'difícil') => {
  emit('update:options', {
    ...props.options,
    difficulty
  })
}

const setRoundsPerPlayer = (rounds: number) => {
  emit('update:options', {
    ...props.options,
    roundsPerPlayer: rounds
  })
}

const setTimerDuration = (duration: number) => {
  emit('update:options', {
    ...props.options,
    timerDuration: duration
  })
}

const setConnectionMode = (mode: 'internet' | 'local') => {
  emit('update:options', {
    ...props.options,
    connectionMode: mode
  })
}
</script>

<template>
  <div v-if="show" class="fixed inset-0 bg-black/70 flex items-center justify-center p-6 z-50">
    <div class="glass rounded-3xl p-6 max-w-md w-full animate-fade-in">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-2xl font-bold text-white">⚙️ Configuración de Sala</h2>
        <button @click="close" class="text-white text-2xl hover:scale-110 transition-transform">✕</button>
      </div>

      <div class="mb-6">
        <label class="block text-teal-200 font-semibold mb-3">🌐 Modo de Conexión</label>
        <div class="grid grid-cols-2 gap-2">
          <button
            @click="setConnectionMode('internet')"
            :class="options.connectionMode === 'internet' ? 'bg-accent text-dark' : 'bg-white/20 text-white'"
            class="py-3 px-4 rounded-xl font-bold transition-colors text-sm"
          >
            🌍 Internet
          </button>
          <button
            @click="setConnectionMode('local')"
            :class="options.connectionMode === 'local' ? 'bg-accent text-dark' : 'bg-white/20 text-white'"
            class="py-3 px-4 rounded-xl font-bold transition-colors text-sm"
          >
            📷 Local (QR)
          </button>
        </div>
        <p class="text-white/60 text-xs mt-2">
          {{ options.connectionMode === 'internet'
            ? 'Jugadores remotos se unen vía servidor (sin escanear QR del guest)'
            : 'Escaneo de códigos QR en persona (modo local)' }}
        </p>
      </div>

      <div class="mb-6">
        <label class="block text-teal-200 font-semibold mb-3">🎰 Modo de Categoría</label>
        <div class="grid grid-cols-2 gap-2 mb-3">
          <button
            @click="setCategoryMode('roulette')"
            :class="options.categoryMode === 'roulette' ? 'bg-accent text-dark' : 'bg-white/20 text-white'"
            class="py-3 px-4 rounded-xl font-bold transition-colors text-sm"
          >
            🎰 Ruleta
          </button>
          <button
            @click="setCategoryMode('select')"
            :class="options.categoryMode === 'select' ? 'bg-accent text-dark' : 'bg-white/20 text-white'"
            class="py-3 px-4 rounded-xl font-bold transition-colors text-sm"
          >
            📂 Manual
          </button>
        </div>
        <p class="text-white/60 text-xs">
          {{ options.categoryMode === 'roulette' ? 'Gira la ruleta para seleccionar categoría' : 'Elige una categoría específica de la lista' }}
        </p>
      </div>

      <div v-if="options.categoryMode === 'select'" class="mb-6">
        <label class="block text-teal-200 font-semibold mb-3">📂 Seleccionar Categoría</label>
        <p class="text-white/60 text-xs mb-2">Elige una categoría específica para la partida</p>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
          <button
            v-for="(cat, key) in categories"
            :key="String(key)"
            @click="selectSingleCategory(String(key))"
            :class="[
              'p-2 rounded-lg text-sm font-medium transition-all',
              options.selectedCategories.length === 1 && options.selectedCategories[0] === key
                ? 'bg-accent text-dark ring-2 ring-accent'
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            ]"
          >
            <span class="text-lg">{{ cat.emoji }}</span>
            <span class="block text-xs mt-1">{{ String(key) }}</span>
          </button>
        </div>
      </div>

      <div v-if="options.categoryMode === 'roulette'" class="mb-6">
        <label class="block text-teal-200 font-semibold mb-3">📂 Categorías para la Ruleta</label>
        <p class="text-white/60 text-xs mb-2">Deja vacío para usar todas las categorías</p>
        <div class="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
          <button
            v-for="(cat, key) in categories"
            :key="String(key)"
            @click="toggleCategory(String(key))"
            :class="[
              'p-2 rounded-lg text-sm font-medium transition-all',
              isCategorySelected(String(key)) 
                ? 'bg-accent text-dark ring-2 ring-accent' 
                : 'bg-white/10 text-white/70 hover:bg-white/20'
            ]"
          >
            <span class="text-lg">{{ cat.emoji }}</span>
            <span class="block text-xs mt-1">{{ String(key) }}</span>
          </button>
        </div>
      </div>

      <div class="mb-6">
        <label class="block text-teal-200 font-semibold mb-3">Dificultad</label>
        <div class="grid grid-cols-3 gap-2">
          <button
            @click="setDifficulty('fácil')"
            :class="options.difficulty === 'fácil' ? 'bg-accent text-dark' : 'bg-white/20 text-white'"
            class="py-3 px-4 rounded-xl font-bold transition-colors"
          >
            Fácil
          </button>
          <button
            @click="setDifficulty('medio')"
            :class="options.difficulty === 'medio' ? 'bg-accent text-dark' : 'bg-white/20 text-white'"
            class="py-3 px-4 rounded-xl font-bold transition-colors"
          >
            Medio
          </button>
          <button
            @click="setDifficulty('difícil')"
            :class="options.difficulty === 'difícil' ? 'bg-accent text-dark' : 'bg-white/20 text-white'"
            class="py-3 px-4 rounded-xl font-bold transition-colors"
          >
            Difícil
          </button>
        </div>
      </div>

      <div class="mb-6">
        <label class="block text-teal-200 font-semibold mb-3">Rondas por jugador</label>
        <div class="grid grid-cols-5 gap-2">
          <button
            v-for="r in [1, 2, 3, 5, 10]"
            :key="r"
            @click="setRoundsPerPlayer(r)"
            :class="options.roundsPerPlayer === r ? 'bg-accent text-dark' : 'bg-white/20 text-white'"
            class="py-3 px-4 rounded-xl font-bold transition-colors"
          >
            {{ r }}
          </button>
        </div>
      </div>

      <div class="mb-6">
        <label class="block text-teal-200 font-semibold mb-3">Duración del turno</label>
        <div class="grid grid-cols-4 gap-2">
          <button
            v-for="t in [30, 60, 90, 120]"
            :key="t"
            @click="setTimerDuration(t)"
            :class="options.timerDuration === t ? 'bg-accent text-dark' : 'bg-white/20 text-white'"
            class="py-3 px-4 rounded-xl font-bold transition-colors"
          >
            {{ t }}s
          </button>
        </div>
      </div>

      <button
        @click="close"
        class="w-full py-4 px-6 bg-accent text-dark font-bold rounded-xl text-lg hover:scale-105 transition-transform"
      >
        ✓ Guardar
      </button>
    </div>
  </div>
</template>